import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { useState, type JSX } from "react";

import { RecurrenceAvailabilityCalendar } from "@/components/recurring-appointment/recurrence-availability-calendar";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { Calendar } from "@/components/ui/calendar/calendar";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { recurringAppointmentFormAtom } from "@/lib/atoms/recurring-appointment-form-atom";
import { RecurrenceFrequency } from "@/lib/enums/recurrence-frequency";
import { formatIgnoringTimezone, getCurrentDate } from "@/lib/helpers/date";

const frequencyLabel: Record<number, string> = {
  [RecurrenceFrequency.Weekly]: "Semanal",
  [RecurrenceFrequency.Biweekly]: "Quinzenal",
};

const RecurringAppointmentAvailabilityPage = (): JSX.Element => {
  const navigate = useNavigate();
  const form = useAtomValue(recurringAppointmentFormAtom);

  const [selectedDate, setSelectedDate] = useState(
    formatIgnoringTimezone(form.startDate ?? getCurrentDate()),
  );

  const subtitle = [
    form.unitAcronym,
    form.appointmentTypeName,
    frequencyLabel[form.frequency],
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <RoundedLayout>
      <RecurrenceCreateHeader
        title="Disponibilidade"
        subtitle={subtitle}
        onEdit={() => {
          void navigate({ to: "/recurring-appointments/create" });
        }}
      />
      <Page>
        <div className="pb-4">
          <Calendar
            initialView="dayGridWeek"
            possibleViews={["dayGridMonth", "dayGridWeek"]}
            selectedDate={selectedDate}
            disabledPastDates
            onDateClick={(date) => setSelectedDate(date)}
          />
        </div>
        <RecurrenceAvailabilityCalendar currentDate={selectedDate} />
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/create/availability")({
  component: RecurringAppointmentAvailabilityPage,
  beforeLoad: () => {
    const form = getDefaultStore().get(recurringAppointmentFormAtom);

    // Passo 1 (padrão) precisa estar concluído: unidade, tipo e forma definidos.
    if (form.unit === 0 || form.appointmentType === 0 || form.paymentMethod === 0) {
      redirect({ to: "/recurring-appointments/create", throw: true });
    }
  },
});
